import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';
import { ButtonModule } from 'primeng/button';
import { MessageModule } from 'primeng/message';
import { TreeModule } from 'primeng/tree';
import { TreeNode } from 'primeng/api';
import { TreeNodeSelectEvent, TreeNodeUnSelectEvent } from 'primeng/types/tree';
import { Measurement, Stage, StageTreeNode } from '@cosmos/shared';

@Component({
  selector: 'cosmos-measurement-change-stage-dialog',
  templateUrl: './measurement-change-stage-dialog.component.html',
  imports: [FormsModule, TranslatePipe, ButtonModule, MessageModule, TreeModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MeasurementChangeStageDialogComponent {
  private readonly ref = inject(DynamicDialogRef);
  private readonly config = inject(DynamicDialogConfig);

  readonly measurement: Measurement = this.config.data.measurement;
  readonly stageTree: StageTreeNode[] = this.config.data.stageTree;

  readonly currentStageId: string = (this.measurement.stage as Stage)?._id ?? (this.measurement.stage as string);

  readonly treeNodes: TreeNode[] = this.buildTree(this.stageTree);

  private readonly nodesById = this.buildNodeIndex(this.treeNodes);
  private readonly stageNamesById = this.buildNameIndex(this.stageTree);

  private readonly currentNode: TreeNode | null = this.nodesById.get(this.currentStageId) ?? null;

  // The tree always has exactly one stage selected — there is no valid
  // "nothing selected" state for this dialog (you're always moving the
  // measurement TO some stage). Pre-selected to the measurement's current
  // stage so the dialog opens showing where it already is. PrimeNG's
  // single-selection mode deselects on a second click of the
  // already-selected node — on unselect the tree falls back to the
  // measurement's current stage (not a re-select of whatever was just
  // clicked away), since that's the only other state that's always valid
  // regardless of which node was unselected.
  readonly selectedNode = signal<TreeNode | null>(this.currentNode);

  readonly selectedStageId = computed(() => this.selectedNode()?.data?.id as string | undefined);

  readonly currentStage = computed(() => ({ name: this.stageNamesById.get(this.currentStageId) }));
  readonly selectedStage = computed(() => ({ name: this.stageNamesById.get(this.selectedStageId() ?? '') }));
  readonly hasChanged = computed(() => this.selectedStageId() !== this.currentStageId);

  // Only stage nodes are ever in this tree (see buildTree) — no measurement
  // leaves, since the picker's job is choosing a destination stage, not a
  // measurement.
  private buildTree(stages: StageTreeNode[]): TreeNode[] {
    return stages.map((s) => ({
      label: s.name,
      data: { type: 'stage', id: s._id },
      children: this.buildTree(s.children),
      icon: 'fa-solid fa-folder',
    }));
  }

  private buildNameIndex(stages: StageTreeNode[], index = new Map<string, string>()): Map<string, string> {
    for (const s of stages) {
      index.set(s._id, s.name);
      this.buildNameIndex(s.children, index);
    }
    return index;
  }

  private buildNodeIndex(nodes: TreeNode[], index = new Map<string, TreeNode>()): Map<string, TreeNode> {
    for (const n of nodes) {
      index.set(n.data.id, n);
      this.buildNodeIndex(n.children ?? [], index);
    }
    return index;
  }

  onNodeSelect(event: TreeNodeSelectEvent): void {
    if (event.node.data?.type !== 'stage') return;
    this.selectedNode.set(event.node);
  }

  // Falls back to the measurement's current stage — see selectedNode's
  // comment for why. Deferred to a microtask: pushing the fallback
  // selection synchronously, inside the same handling pass PrimeNG uses to
  // apply its own (now-unselected) internal state, gets absorbed rather
  // than producing a fresh re-render; deferring lets that pass finish first.
  onNodeUnselect(event: TreeNodeUnSelectEvent): void {
    queueMicrotask(() => this.selectedNode.set(this.currentNode ? { ...this.currentNode } : null));
  }

  confirm() {
    const stageId = this.selectedStageId();
    if (!stageId) return;
    this.ref.close({ stage: stageId });
  }

  cancel() {
    this.ref.close(undefined);
  }
}
